import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Zap, 
  Plus, 
  Trash2, 
  ExternalLink, 
  Copy, 
  Check,
  Loader2,
  BarChart3,
  TrendingUp,
  Search,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [deployments, setDeployments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
      toast.error('Please sign in to access the dashboard');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        const [deploymentsRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/deployments`),
          axios.get(`${API}/analytics/dashboard`).catch(() => ({ data: null })),
        ]);
        setDeployments(deploymentsRes.data);
        setAnalytics(analyticsRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/deployments/${deleteId}`);
      setDeployments(prev => prev.filter(d => d.id !== deleteId));
      toast.success('Deployment removed');
    } catch (error) {
      toast.error('Failed to remove deployment');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
          <div>
            <h1 className="font-heading text-4xl font-bold text-white mb-2">Dashboard</h1>
            <p className="text-slate-400">Manage your connector deployments</p>
          </div>
          <Link to="/marketplace">
            <Button className="glow-primary bg-primary hover:bg-primary/90" data-testid="add-connector-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Connector
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        {analytics && (
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <Zap className="w-8 h-8 text-primary" />
                <Badge variant="outline" className="border-primary/30 text-primary">Active</Badge>
              </div>
              <div className="text-3xl font-heading font-bold text-white">{deployments.length}</div>
              <p className="text-sm text-slate-500">My Deployments</p>
            </div>
            
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <BarChart3 className="w-8 h-8 text-accent mb-2" />
              <div className="text-3xl font-heading font-bold text-white">{analytics.total_connectors}</div>
              <p className="text-sm text-slate-500">Total Connectors</p>
            </div>
            
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <TrendingUp className="w-8 h-8 text-blue-400 mb-2" />
              <div className="text-3xl font-heading font-bold text-white">{analytics.total_deployments.toLocaleString()}</div>
              <p className="text-sm text-slate-500">Platform Deployments</p>
            </div>
            
            <div className="bg-card border border-white/5 rounded-xl p-6">
              <Search className="w-8 h-8 text-amber-400 mb-2" />
              <div className="text-3xl font-heading font-bold text-white">{analytics.top_searched?.length || 0}</div>
              <p className="text-sm text-slate-500">Trending Searches</p>
            </div>
          </div>
        )}

        {/* Deployments Table */}
        <div className="bg-card border border-white/5 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5">
            <h2 className="font-heading font-semibold text-white">My Deployments</h2>
          </div>
          
          {deployments.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-6 flex items-center justify-center">
                <Zap className="w-8 h-8 text-slate-500" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">No deployments yet</h3>
              <p className="text-slate-400 mb-6">Deploy your first connector to get started</p>
              <Link to="/marketplace">
                <Button className="bg-primary hover:bg-primary/90">
                  Browse Marketplace
                </Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-slate-400">Connector</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">API Key</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Created</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deployments.map((deployment) => (
                  <TableRow key={deployment.id} className="border-white/5">
                    <TableCell>
                      <Link 
                        to={`/connector/${deployment.connector_id}`}
                        className="font-medium text-white hover:text-primary transition-colors"
                      >
                        {deployment.connector_name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-slate-700 text-slate-300 capitalize">
                        {deployment.deployment_type.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-sm text-slate-400 font-mono">
                          {deployment.api_key?.substring(0, 12)}...
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(deployment.api_key, deployment.id)}
                          className="h-8 w-8 p-0 text-slate-500 hover:text-white"
                        >
                          {copied === deployment.id ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-accent/20 text-accent border-accent/30">
                        {deployment.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-400">
                      {new Date(deployment.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/sandbox/${deployment.connector_id}`}>
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-slate-400 hover:text-destructive"
                          onClick={() => setDeleteId(deployment.id)}
                          data-testid={`delete-deployment-${deployment.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Demand Intelligence */}
        {analytics && analytics.missing_integrations?.length > 0 && (
          <div className="mt-12 bg-card border border-white/5 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-6 h-6 text-amber-400" />
              <h2 className="font-heading font-semibold text-white">Demand Intelligence</h2>
            </div>
            <p className="text-slate-400 mb-4">Most requested integrations that don't exist yet:</p>
            <div className="flex flex-wrap gap-3">
              {analytics.missing_integrations.map((item, index) => (
                <Badge 
                  key={index}
                  variant="outline" 
                  className="border-amber-900/50 text-amber-400 bg-amber-950/30 px-4 py-2"
                >
                  {item.software_name}
                  <span className="ml-2 text-amber-600">({item.request_count} requests)</span>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Remove Deployment?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This will remove the deployment from your account. The connector will still be available in the marketplace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700 text-slate-300 hover:bg-secondary">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DashboardPage;
